/**
 * SEO hook for dynamic page titles and meta tags
 * Updates document title and meta tags for SPA navigation
 *
 * @example
 * useSEO({
 *   title: 'Meus Palpites',
 *   description: 'Veja e faca seus palpites nos jogos da Copa do Mundo 2026',
 * });
 */

import { useEffect } from 'react';

const DEFAULT_SITE_NAME = 'Bolão 2026';
const DEFAULT_DESCRIPTION =
  'Participe do maior bolão da Copa do Mundo 2026! Faça seus palpites nos jogos, crie grupos com amigos e concorra a prêmios.';
const CANONICAL_DOMAIN = 'https://bolao2026.net';

interface SEOProps {
  /** Page title (will be appended with site name) */
  title?: string;
  /** Page description for meta tag */
  description?: string;
  /** Override canonical URL path (defaults to current path) */
  canonicalPath?: string;
  /** Additional keywords for the page */
  keywords?: readonly string[];
  /** Disable indexing for this page */
  noIndex?: boolean;
  /** JSON-LD structured data to inject */
  structuredData?: Record<string, unknown>;
}

/**
 * Updates document title and meta tags for SEO
 * Restores defaults on unmount
 */
export function useSEO({
  title,
  description = DEFAULT_DESCRIPTION,
  canonicalPath,
  keywords,
  noIndex = false,
  structuredData,
}: SEOProps = {}): void {
  useEffect(() => {
    // Store original values for cleanup
    const originalTitle = document.title;
    const originalDescription = document
      .querySelector('meta[name="description"]')
      ?.getAttribute('content');
    const originalCanonical = document
      .querySelector('link[rel="canonical"]')
      ?.getAttribute('href');

    // Update title
    const fullTitle = title
      ? `${title} | ${DEFAULT_SITE_NAME}`
      : `${DEFAULT_SITE_NAME} - Copa do Mundo FIFA 2026`;
    document.title = fullTitle;

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }

    // Update OG title and description
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', title || DEFAULT_SITE_NAME);
    }

    const ogDescription = document.querySelector(
      'meta[property="og:description"]'
    );
    if (ogDescription) {
      ogDescription.setAttribute('content', description);
    }

    // Update Twitter title and description
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', title || DEFAULT_SITE_NAME);
    }

    const twitterDescription = document.querySelector(
      'meta[name="twitter:description"]'
    );
    if (twitterDescription) {
      twitterDescription.setAttribute('content', description);
    }

    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      const path = canonicalPath || window.location.pathname;
      canonical.setAttribute('href', `${CANONICAL_DOMAIN}${path}`);
    }

    // Update OG URL
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      const path = canonicalPath || window.location.pathname;
      ogUrl.setAttribute('href', `${CANONICAL_DOMAIN}${path}`);
    }

    // Update keywords if provided
    if (keywords && keywords.length > 0) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      const baseKeywords =
        'bolão copa do mundo 2026, palpites copa 2026, apostas copa do mundo';
      metaKeywords.setAttribute(
        'content',
        `${baseKeywords}, ${keywords.join(', ')}`
      );
    }

    // Handle noIndex
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (noIndex) {
      if (metaRobots) {
        metaRobots.setAttribute('content', 'noindex, nofollow');
      }
    } else if (metaRobots) {
      metaRobots.setAttribute(
        'content',
        'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
      );
    }

    // Inject structured data if provided
    let structuredDataScript: HTMLScriptElement | null = null;
    if (structuredData) {
      structuredDataScript = document.createElement('script');
      structuredDataScript.type = 'application/ld+json';
      structuredDataScript.id = 'dynamic-structured-data';
      structuredDataScript.textContent = JSON.stringify(structuredData);

      // Remove existing dynamic structured data
      const existingScript = document.getElementById('dynamic-structured-data');
      if (existingScript) {
        existingScript.remove();
      }

      document.head.appendChild(structuredDataScript);
    }

    // Cleanup on unmount - restore original values
    return () => {
      document.title = originalTitle;

      if (metaDescription && originalDescription) {
        metaDescription.setAttribute('content', originalDescription);
      }

      if (canonical && originalCanonical) {
        canonical.setAttribute('href', originalCanonical);
      }

      // Remove dynamic structured data
      if (structuredDataScript) {
        structuredDataScript.remove();
      }
    };
  }, [title, description, canonicalPath, keywords, noIndex, structuredData]);
}

/**
 * SEO configuration for common pages
 * Use these presets for consistent SEO across the app
 */
export const SEO_CONFIGS = {
  landing: {
    title: undefined, // Uses default
    description: DEFAULT_DESCRIPTION,
  },
  login: {
    title: 'Entrar',
    description:
      'Faça login no Bolão 2026 para acessar seus palpites e acompanhar o ranking.',
    noIndex: true,
  },
  register: {
    title: 'Criar conta',
    description:
      'Crie sua conta no Bolão 2026 e participe do maior bolão da Copa do Mundo 2026!',
  },
  dashboard: {
    title: 'Início',
    description:
      'Veja seus palpites, acompanhe o ranking e participe dos bolões da Copa do Mundo 2026.',
    noIndex: true,
  },
  matches: {
    title: 'Jogos',
    description:
      'Veja todos os jogos da Copa do Mundo 2026 e faça seus palpites.',
    keywords: ['jogos copa 2026', 'calendario copa do mundo', 'partidas fifa'],
  },
  pools: {
    title: 'Bolões',
    description:
      'Crie ou participe de bolões da Copa do Mundo 2026 com seus amigos.',
    keywords: ['bolões copa 2026', 'apostas com amigos', 'grupo de palpites'],
  },
  profile: {
    title: 'Meu perfil',
    description: 'Gerencie seu perfil e configurações no Bolão 2026.',
    noIndex: true,
  },
  security: {
    title: 'Segurança',
    description:
      'Configure a autenticação de dois fatores e outras opções de segurança.',
    noIndex: true,
  },
  forgotPassword: {
    title: 'Recuperar senha',
    description: 'Recupere sua senha do Bolão 2026.',
    noIndex: true,
  },
  notFound: {
    title: 'Página não encontrada',
    description: 'A página que você procura não existe.',
    noIndex: true,
  },
} as const;

export default useSEO;
